import {
  useGetFortnoxSalaryCacheQuery,
  useRefreshFortnoxSalaryCacheMutation
} from "@bonliva-crm/graphql";
import locale from "@bonliva-crm/locale";
import { useSnackbarsPipeline } from "@bonliva-crm/ui";
import { Spinner } from "@common";
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import { toSEK } from "@utils/formatCurrency";
import { format } from "date-fns";

type Props = {
  consultantId: string;
  isActive?: boolean;
};

export const WorkStatistics = (props: Props) => {
  const snack = useSnackbarsPipeline();

  const {
    data: fortnoxData,
    loading: fortnoxLoading,
    refetch: refetchFortnox
  } = useGetFortnoxSalaryCacheQuery({
    variables: { id: props.consultantId },
    fetchPolicy: "network-only",
    skip: !props.isActive,
    onError: () => snack.showError(locale.ErrorOccurred)
  });

  const [refreshCache, { loading: refreshing }] = useRefreshFortnoxSalaryCacheMutation({
    variables: { id: props.consultantId },
    onCompleted: () => {
      refetchFortnox();
      snack.showSuccess("Fortnox lönedata uppdaterad");
    },
    onError: () => snack.showError(locale.ErrorOccurred)
  });

  const years = getYears();
  const cache = fortnoxData?.getFortnoxSalaryCache;

  const content = () => {
    if (fortnoxLoading) {
      return (
        <Box display="flex" justifyContent="center" p={2}>
          <Spinner size={32} inline />
        </Box>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableCell width="10rem">{locale.Year}</TableCell>
          <TableCell>{locale.Salary}</TableCell>
        </TableHead>
        <TableBody>
          {years.map((year) => {
            const record = cache?.yearSums?.find((s) => s.year === year);
            return (
              <TableRow key={year}>
                <TableCell>{year}</TableCell>
                <TableCell>{record ? toSEK(record.sum) : "—"}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  };

  return (
    <Card style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Typography variant="body1">
        Siffran som visas här speglar enbart den lön eller sjuklön som är utbetald för
        arbete som är gjort på den nationella upphandlingen. Siffran speglar:
        <ul>
          <li>Bruttolön inklusive semesterersättning från arbetade timmar</li>
          <li>
            Bruttolön inklusive semesterersättning från övrig bruttolön (så som exempelvis
            bonusas)
          </li>
          <li>Utlägg som ej relaterar till arbete</li>
          <li>Boende- och resebidrag</li>
          <li>Sjuklön</li>
        </ul>
        Ett underlag måste vara utbetalt för att lönen ska adderas till denna siffra.
        Underlag som är skapade men ej ännu utbetalda räknas alltså inte med.
      </Typography>
      <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ gap: "1rem" }}>
        {cache?.lastFetchedAt && (
          <Typography variant="caption" color="textSecondary">
            Hämtad: {format(new Date(Number(cache.lastFetchedAt)), "yyyy-MM-dd HH:mm")}
          </Typography>
        )}
        <Button
          variant="outlined"
          size="small"
          onClick={() => refreshCache()}
          disabled={refreshing}
        >
          {refreshing ? <Spinner size={16} inline /> : "Uppdatera"}
        </Button>
      </Box>
      {content()}
    </Card>
  );
};

function getYears() {
  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = 2023; i <= currentYear + 1; i++) {
    years.push(i);
  }
  return years;
}
